import {
  SavePtacQuotableSystemResponseType,
  SelectedSystemType,
  ValidPtacSystemsResponseType
} from '@alpinehomeair/common';
import { createAsyncThunk } from '@reduxjs/toolkit';
import { RootState } from 'app/store';
import { SystemOptionType } from 'data/types';
import {
  addSelectedSystem,
  settleCustomSystemsRevision
} from 'features/systems/systems-slice';
import axios from 'utils/api';
import {
  PTAC_QUOTABLE_SYSTEM_URL,
  ptacQuotableSystemBody,
  ptacUnitPicksMatchConfiguration
} from 'utils/ptac-quotable-system';
import { buildSelectedSystemFromPtacOption } from 'utils/ptac-selected-system';
import {
  buildPtacSystemsRequestBody,
  PTAC_SYSTEMS_URL
} from 'utils/ptac-systems-request';
import { PtacSystemsRequestParams } from 'utils/ptac-systems-request-types';
import {
  beginPtacSystemsFetch,
  emptyPtacSystemsFilterUniverse,
  failPtacSystemsFetch,
  finishPtacSystemsFetch
} from './ptac-selection-slice';

// One request per PTAC design: the brand/series systems the server composes
// from the units' loads. Deduped on an identical in-flight body; a stale
// finish is dropped by the slice's hash guard.
export const fetchPtacSystems = createAsyncThunk(
  'ptacSelection/fetchSystems',
  async (params: PtacSystemsRequestParams, { dispatch, getState }) => {
    const body = buildPtacSystemsRequestBody(params);
    const requestBodyHash = JSON.stringify({
      ...body,
      customSystemsRevision: params.customSystemsRevision
    });
    const { systems } = (getState() as RootState).ptacSelection;
    if (
      systems.requestBodyHash === requestBodyHash &&
      systems.status !== 'error'
    ) {
      return;
    }

    dispatch(beginPtacSystemsFetch({ requestBodyHash }));
    try {
      const response = await axios.post<ValidPtacSystemsResponseType>(
        PTAC_SYSTEMS_URL,
        body
      );
      dispatch(
        finishPtacSystemsFetch({
          requestBodyHash,
          data: {
            options: response.data.systems ?? [],
            unitPicksByOptionId: response.data.unitPicksByOptionId ?? {},
            alternativesByOptionId: response.data.alternativesByOptionId ?? {},
            filterUniverse:
              response.data.filterUniverse ?? emptyPtacSystemsFilterUniverse
          }
        })
      );
      dispatch(
        settleCustomSystemsRevision({
          configId: params.configuration.uniqueId,
          revision: params.customSystemsRevision
        })
      );
    } catch (error) {
      console.error('[ptac-selection] systems fetch failed:', error);
      dispatch(failPtacSystemsFetch({ requestBodyHash }));
    }
  }
);

export const addPtacSystemToQuotable = createAsyncThunk<
  SelectedSystemType,
  SystemOptionType,
  { state: RootState }
>(
  'ptacSelection/addSystemToQuotable',
  async (option, { dispatch, getState }) => {
    const state = getState();
    const unitPicks =
      state.ptacSelection.systems.unitPicksByOptionId[option.id] ?? [];
    if (unitPicks.length === 0) {
      throw new Error(`No unit picks are loaded for system ${option.id}`);
    }
    const configuration = state.configurations.configurations.find(
      (entry) => entry.uniqueId === option.configuration.uniqueId
    );
    if (
      !configuration ||
      !ptacUnitPicksMatchConfiguration(configuration, unitPicks)
    ) {
      throw new Error(
        `The unit picks for system ${option.id} no longer match the PTAC design`
      );
    }
    const response = await axios.put<SavePtacQuotableSystemResponseType>(
      PTAC_QUOTABLE_SYSTEM_URL,
      ptacQuotableSystemBody(option, unitPicks)
    );
    const selectedSystem = buildSelectedSystemFromPtacOption({
      option,
      quotableSystem: response.data.quotableSystem
    });
    dispatch(addSelectedSystem(selectedSystem));
    return selectedSystem;
  }
);
